// src/App.js
// Ensure Firebase initializes once for the whole app.
import "./firebase";

import React, { useEffect, useMemo, useState, createContext, useContext, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider, CssBaseline, CircularProgress } from "@mui/material";
import { createKbeanTheme } from "./theme";
import { AuthProvider } from "./auth/AuthContext";
import { getAuth, onAuthStateChanged } from "firebase/auth";

import MainLayout from "./components/layout/MainLayout";

import { contentRoutes } from "./routes/contentRoutes";
import { BUSINESS_HUB_PATH, PLAYLISTS_PATH } from "./services/postAuthRouting";

import RequireAdmin from "./components/routes/RequireAdmin";
import RequireCreator from "./components/routes/RequireCreator";
import RequireCollectionAccess from "./components/routes/RequireCollectionAccess";
import RequireRaffleAccess from "./components/routes/RequireRaffleAccess";
import RequireBusinessAnalyticsAccess from "./components/routes/RequireBusinessAnalyticsAccess";
import OfflineLockOverlay from "./components/network/OfflineLockOverlay";
import { useAuth } from "./auth/AuthContext";
import useAppVersionPoller from "./hooks/useAppVersionPoller";

// Route components are lazy-loaded so page-specific heavy dependencies (charts,
// maps, drag-and-drop, date pickers, wizards, the Azure blob SDK) ship as
// on-demand chunks instead of bloating the initial bundle.
const LandingPage = React.lazy(() => import("./pages/LandingPage"));
const AccountSettingsPage = React.lazy(() => import("./pages/AccountSettingsPage"));
const DashboardPage = React.lazy(() => import("./pages/DashboardPage"));
const BusinessAnalyticsPage = React.lazy(() => import("./pages/BusinessAnalyticsPage"));
const CollectionsPage = React.lazy(() => import("./pages/CollectionsPage"));
const TimeCapsulesPage = React.lazy(() => import("./pages/TimeCapsulesPage"));
const TitlesPage = React.lazy(() => import("./pages/TitlesPage"));
const TVRailsPage = React.lazy(() => import("./pages/TVRailsPage"));
const InteractiveEditorPage = React.lazy(() => import("./pages/InteractiveEditorPage"));
const ChallengesPage = React.lazy(() => import("./pages/ChallengesPage"));
const ChallengeMakerPage = React.lazy(() => import("./pages/ChallengeMakerPage"));
const RafflesPage = React.lazy(() => import("./pages/RafflesPage"));
const CouponsPage = React.lazy(() => import("./pages/CouponsPage"));
const MomentMakerPage = React.lazy(() => import("./pages/MomentMakerPage"));
const ExecutiveAnalyticsPage = React.lazy(() => import("./pages/ExecutiveAnalyticsPage"));
const ExecutiveBusinessDetailPage = React.lazy(() => import("./pages/ExecutiveBusinessDetailPage"));
const ExecutiveBusinessPerformancePage = React.lazy(() => import("./pages/ExecutiveBusinessPerformancePage"));
const CapsuleAdminPage = React.lazy(() => import("./components/pages/admin/CapsuleAdminPage"));
const AdminSettingsPage = React.lazy(() => import("./components/pages/admin/AdminSettingsPage"));
const UserAdminPage = React.lazy(() => import("./components/pages/admin/UserAdminPage"));
const CampaignsAdminPage = React.lazy(() => import("./components/pages/admin/CampaignsAdminPage"));
const PointsAdminPage = React.lazy(() => import("./components/pages/admin/points-admin/PointsAdminPage"));
const PromoAdminPage = React.lazy(() => import("./components/pages/admin/promo-admin/PromoAdminPage"));
const PinMakerPage = React.lazy(() => import("./components/pages/admin/pin-maker/PinMakerPage"));
const PublicRaffleClaimPage = React.lazy(() => import("./pages/PublicRaffleClaimPage"));
const RaffleTicketPage = React.lazy(() => import("./pages/RaffleTicketPage"));
const RaffleRedeemPage = React.lazy(() => import("./pages/RaffleRedeemPage"));
const SignInPage = React.lazy(() => import("./pages/auth/SignInPage"));
const SignUpPage = React.lazy(() => import("./pages/auth/SignUpPage"));
const PairPage = React.lazy(() => import("./pages/auth/PairPage"));
const AccountTypePage = React.lazy(() => import("./pages/auth/AccountTypePage"));
const BusinessOnboardingPage = React.lazy(() => import("./pages/auth/BusinessOnboardingPage"));
const BusinessInvitationsPage = React.lazy(() => import("./pages/BusinessInvitationsPage"));
const TvManagerPage = React.lazy(() => import("./pages/TvManagerPage"));

function NotFound() {
  return <div style={{ padding: 24 }}>Not found</div>;
}

// Shown briefly while a lazy-loaded route chunk is being fetched.
function RouteFallback() {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "60vh",
        width: "100%",
      }}
    >
      <CircularProgress size={28} />
    </div>
  );
}

function RootIndexRoute() {
  const { user, loading } = useAuth();

  if (loading) return null;
  if (!user) return <Navigate to="/signin" replace />;
  return <LandingPage />;
}

export const ColorModeContext = createContext({ mode: "dark", toggle: () => { } });
export const useColorMode = () => useContext(ColorModeContext);

export default function App() {
  // Auto-reload the tab when a newer build is deployed.
  useAppVersionPoller();

  const [mode, setMode] = useState(() => localStorage.getItem("kbean-theme-mode") || "dark");
  const [isOnline, setIsOnline] = useState(() =>
    typeof navigator === "undefined" ? true : navigator.onLine
  );
  const [lastOnlineAt, setLastOnlineAt] = useState(() => new Date());
  useEffect(() => { localStorage.setItem("kbean-theme-mode", mode); }, [mode]);
  useEffect(() => {
    document.body.setAttribute("data-theme", mode);
    return () => {
      document.body.removeAttribute("data-theme");
    };
  }, [mode]);

  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, (user) => {
      if (!user) {
        // eslint-disable-next-line no-console
        console.log("[Auth] No user signed in.");
        return;
      }
      user.getIdTokenResult(true).then((r) => {
        // eslint-disable-next-line no-console
        console.log("[Auth] UID:", r.claims.user_id || user.uid);
        // eslint-disable-next-line no-console
        console.log("[Auth] Claims:", r.claims);
      });
    });
    return () => unsub();
  }, []);
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setLastOnlineAt(new Date());
    };
    const handleOffline = () => {
      setIsOnline(false);
    };
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const colorMode = useMemo(
    () => ({ mode, toggle: () => setMode((m) => (m === "dark" ? "light" : "dark")) }),
    [mode]
  );
  const theme = useMemo(() => createKbeanTheme(mode), [mode]);

  return (
    <Router>
      <AuthProvider>
        <ColorModeContext.Provider value={colorMode}>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <OfflineLockOverlay
              open={!isOnline}
              lastOnlineAt={lastOnlineAt}
              onRetry={() => window.location.reload()}
            />
            <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/pair" element={<PairPage />} />
              <Route path="/raffles/c/:campaignId" element={<PublicRaffleClaimPage />} />
              <Route path="/coupons/c/:campaignId" element={<PublicRaffleClaimPage />} />
              <Route path="/coupons/t/:ticketToken" element={<RaffleTicketPage />} />
              <Route path="/raffles/t/:ticketToken" element={<RaffleTicketPage />} />
              <Route path="/redeem/t/:ticketToken" element={<RaffleRedeemPage />} />
              <Route element={<MainLayout />}>
                {/* Authentication Routes */}
                <Route path="/signin" element={<SignInPage />} />
                <Route path="/connect" element={<SignInPage />} />
                <Route path="/signup" element={<SignUpPage />} />
                <Route path="/account-type" element={<AccountTypePage />} />
                <Route path={BUSINESS_HUB_PATH} element={<BusinessOnboardingPage />} />
                <Route path="/business-invitations" element={<BusinessInvitationsPage />} />
                <Route path="/admin/sponsored-pin-template" element={<PinMakerPage />} />

                {/* Public/creator-access pages */}
                <Route index element={<RootIndexRoute />} />
                <Route path="/account" element={<AccountSettingsPage />} />
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route element={<RequireBusinessAnalyticsAccess />}>
                  <Route path="/analytics" element={<BusinessAnalyticsPage />} />
                </Route>

                <Route element={<RequireCreator />}>
                  {contentRoutes.map((r) => (
                    <Route key={r.path} path={r.path} element={r.element} />
                  ))}
                  <Route path="/" element={<Navigate to="/content" replace />} />
                </Route>

                {/* Admin + Business: collection curation */}
                <Route element={<RequireCollectionAccess />}>
                  <Route path={PLAYLISTS_PATH} element={<CollectionsPage />} />
                  <Route path="/inssa-live" element={<TvManagerPage />} />
                  <Route path="/time-capsules" element={<TimeCapsulesPage />} />
                  <Route path="/timecapsule" element={<TimeCapsulesPage />} />
                  <Route path="moment-maker" element={<MomentMakerPage />} />
                </Route>

                <Route element={<RequireRaffleAccess />}>
                  <Route path="/raffles" element={<RafflesPage />} />
                  <Route path="/coupons" element={<CouponsPage />} />
                </Route>

                {/* Admin-only: TV publishing + advanced tools */}
                <Route element={<RequireAdmin />}>
                  <Route path="admin/settings" element={<AdminSettingsPage />} />
                  <Route path="admin/users" element={<UserAdminPage />} />
                  <Route path="admin/identity" element={<Navigate to="/admin/users?tool=identity" replace />} />
                  <Route path="admin/campaigns" element={<CampaignsAdminPage />} />
                  <Route path="admin/points" element={<PointsAdminPage />} />
                  <Route path="admin/promos" element={<PromoAdminPage />} />
                  <Route path="admin/analytics" element={<ExecutiveAnalyticsPage />} />
                  <Route path="admin/analytics/businesses" element={<ExecutiveBusinessPerformancePage />} />
                  <Route path="admin/analytics/businesses/:businessId" element={<ExecutiveBusinessDetailPage />} />
                  <Route path="admin/timecapsules" element={<CapsuleAdminPage />} />
                  <Route path="titles" element={<TitlesPage />} />
                  <Route path="interactive-editor" element={<InteractiveEditorPage />} />
                  <Route path="tv-rails" element={<TVRailsPage />} />
                  <Route path="challenges" element={<ChallengesPage />} />
                  <Route path="challenge-maker" element={<ChallengeMakerPage />} />
                  {/* NEW: allow /tv-rails/new?fromCollection=... to render the same page (it will open the create dialog) */}
                  <Route path="tv-rails/new" element={<TVRailsPage />} />
                </Route>

                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
            </Suspense>
          </ThemeProvider>
        </ColorModeContext.Provider>
      </AuthProvider>
    </Router>
  );
}
